/**
 * Phase2TaskGrid.tsx — FEAT-2026-0007 6C.1 / 6F.4
 * Read-only machine-column task grid for Phase-2 (Employee planning).
 * 6F.4 adds optional bulk-select mode (checkbox column + header select-all).
 */

import React from 'react';
import {
  Box,
  Checkbox,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import { PHASE2_TASK_COLUMNS } from './phase2TaskColumns';
import { computeTaskDurationDays, taskDurationDisplay } from './taskDuration';
import type { TaskWithBookings, TaskAssignee } from '../../../../services/scheduleTasks';
import type { AssignableUser } from '../../../../services/scheduleAssignments';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Group a task's assignees by the machine they staff (079 MachineBookingKey),
 * deduped by user. NULL-booking rows fall under "All machines". Read-only — the
 * Phase-2 top grid is informational; all add/remove happens in the per-machine
 * weekly section below (2026-06-20).
 */
function groupAssigneesByMachine(
  assignees: TaskAssignee[],
  bookings: TaskWithBookings['Bookings'],
): Array<{ key: string; label: string; names: string[] }> {
  const nameByBooking = new Map(
    bookings.map((b) => [b.MachineBookingKey, b.MachineName ?? b.MachineCode ?? 'Machine']),
  );
  const groups = new Map<string, { label: string; users: Map<string, string> }>();
  for (const a of assignees) {
    const bk = a.MachineBookingKey ?? '__task__';
    const label = bk === '__task__' ? 'All machines' : (nameByBooking.get(bk) ?? 'Machine');
    const g = groups.get(bk) ?? { label, users: new Map<string, string>() };
    g.users.set(a.UserKey, a.displayName);
    groups.set(bk, g);
  }
  return [...groups.entries()].map(([key, g]) => ({ key, label: g.label, names: [...g.users.values()] }));
}

/** Format an ISO date string to a short human-readable form. */
function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(m, 10) - 1]} ${parseInt(d, 10)}, ${y}`;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface Phase2TaskGridProps {
  tasks: TaskWithBookings[];
  assigneesByTask: Record<string, TaskAssignee[]>;
  scenarioKey: string | null;
  /** Called after a successful assignment/unassignment so the parent can reload. */
  onReloadAssignees: (taskKey: string) => void | Promise<void>;
  /** Called after any allocation change so table-3 capacity can refetch (6C.5). */
  onAllocationChanged?: () => void;
  /** Called when InlineAssigneePicker needs to escalate to the full dialog. */
  onEscalateToDialog: (task: TaskWithBookings, preselectUser?: AssignableUser) => void;
  // ── 6F.4 bulk-select props (optional) ──────────────────────────────────────
  /** When true, shows a checkbox column for row selection. */
  bulkMode?: boolean;
  /** Set of currently-selected ScheduleTaskKeys. */
  selectedKeys?: Set<string>;
  /** Called when a row checkbox changes. */
  onToggleSelect?: (key: string) => void;
  /** Called when the header select-all checkbox changes. */
  onSelectAll?: () => void;
  /** Stat-holiday ISO dates — so the Duration column counts working days only. */
  holidays?: readonly string[];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const Phase2TaskGrid: React.FC<Phase2TaskGridProps> = ({
  tasks,
  assigneesByTask,
  bulkMode = false,
  selectedKeys = new Set<string>(),
  onToggleSelect,
  onSelectAll,
  holidays = [],
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        border: `1px solid ${theme.border.color}`,
        borderRadius: `${theme.border.radius}px`,
        overflow: 'auto',
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {/* 6F.4 — select-all checkbox when in bulk mode */}
            {bulkMode && (
              <TableCell
                padding="checkbox"
                sx={{
                  backgroundColor: theme.table.headerBg,
                  borderBottom: `2px solid ${theme.border.color}`,
                }}
              >
                <Checkbox
                  size="small"
                  indeterminate={
                    selectedKeys.size > 0 && selectedKeys.size < tasks.length
                  }
                  checked={selectedKeys.size === tasks.length && tasks.length > 0}
                  onChange={onSelectAll}
                />
              </TableCell>
            )}
            {PHASE2_TASK_COLUMNS.map((col) => (
              <TableCell
                key={col.id}
                align={col.align ?? 'left'}
                sx={{
                  width: col.width,
                  backgroundColor: theme.table.headerBg,
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  letterSpacing: 0.3,
                  py: 1,
                  px: 1.5,
                  borderBottom: `2px solid ${theme.border.color}`,
                  whiteSpace: 'nowrap',
                }}
              >
                {col.tooltip ? (
                  <Tooltip title={col.tooltip} placement="top">
                    <span>{col.label}</span>
                  </Tooltip>
                ) : (
                  col.label
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {tasks.map((task, idx) => {
            const assignees = assigneesByTask[task.ScheduleTaskKey] ?? [];
            // Run Mode Phase C: continuous tasks show 'N cal days' (24/7).
            const duration = computeTaskDurationDays(task.StartDate, task.EndDate, holidays, task.RunMode);
            const durationDisplay = taskDurationDisplay(duration, task.RunMode);
            const statusColor =
              task.Bookings.some((b) => b.Status === 'Committed')
                ? theme.schedule.committed
                : theme.schedule.tentative;

            const assigneeGroups = groupAssigneesByMachine(assignees, task.Bookings);

            return (
              <TableRow
                key={task.ScheduleTaskKey}
                hover
                selected={bulkMode && selectedKeys.has(task.ScheduleTaskKey)}
                sx={{ '& td': { py: 1, px: 1.5 } }}
              >
                {/* 6F.4 — per-row checkbox */}
                {bulkMode && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={selectedKeys.has(task.ScheduleTaskKey)}
                      onChange={() => onToggleSelect?.(task.ScheduleTaskKey)}
                    />
                  </TableCell>
                )}

                {/* # */}
                <TableCell align="center">
                  <Typography variant="caption" color="text.secondary">
                    {idx + 1}
                  </Typography>
                </TableCell>

                {/* Task Name */}
                <TableCell>
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.82rem' }}>
                    {task.Name}
                  </Typography>
                  {task.TaskTypeName && (
                    <Typography variant="caption" color="text.secondary">
                      {task.TaskTypeName}
                    </Typography>
                  )}
                </TableCell>

                {/* Test Qty — read-only */}
                <TableCell align="center">
                  <Typography variant="body2" sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>
                    {task.TestQty != null ? task.TestQty : '—'}
                  </Typography>
                </TableCell>

                {/* Machine Qty — read-only in Phase 2 */}
                <TableCell align="center">
                  <Typography variant="body2" sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>
                    {task.MachineCount}
                  </Typography>
                </TableCell>

                {/* Assigned Machine(s) — read-only in Phase 2 */}
                <TableCell>
                  {task.Bookings.length === 0 ? (
                    <Typography
                      variant="caption"
                      sx={{ color: theme.schedule.conflictSoft, fontStyle: 'italic' }}
                    >
                      No machine assigned
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {task.Bookings.map((booking) => (
                        <Box
                          key={booking.MachineBookingKey}
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                        >
                          <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>
                            {booking.MachineName ?? booking.MachineCode ?? '—'}
                          </Typography>
                          <Chip
                            label={booking.Status}
                            size="small"
                            sx={{
                              fontSize: '0.65rem',
                              height: 18,
                              backgroundColor: alpha(statusColor, 0.12),
                              color: statusColor,
                              flexShrink: 0,
                            }}
                          />
                        </Box>
                      ))}
                    </Box>
                  )}
                </TableCell>

                {/* Start Date — read-only in Phase 2 */}
                <TableCell align="center">
                  <Typography variant="body2" sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                    {fmtDate(task.StartDate)}
                  </Typography>
                </TableCell>

                {/* End Date — read-only */}
                <TableCell align="center">
                  <Typography variant="body2" sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                    {fmtDate(task.EndDate)}
                  </Typography>
                </TableCell>

                {/* Duration — computed, read-only (per-mode unit — Phase C) */}
                <TableCell align="center">
                  <Tooltip title={durationDisplay.tooltip}>
                    <Typography variant="body2" sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>
                      {durationDisplay.text}
                    </Typography>
                  </Tooltip>
                </TableCell>

                {/* Assigned Employees — read-only summary grouped by machine.
                    Add/remove now lives in the per-machine weekly section below. */}
                <TableCell>
                  {assigneeGroups.length === 0 ? (
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.disabled', fontStyle: 'italic', fontSize: '0.72rem' }}
                    >
                      Assign in the schedule below ↓
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {assigneeGroups.map((g) => (
                        <Box key={g.key} sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap' }}>
                          <Typography
                            variant="caption"
                            sx={{ fontSize: '0.62rem', color: 'text.secondary', minWidth: 0, flexShrink: 0 }}
                          >
                            {g.label}:
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25 }}>
                            {g.names.map((name) => (
                              <Chip
                                key={name}
                                label={name}
                                size="small"
                                sx={{
                                  fontSize: '0.68rem',
                                  height: 20,
                                  backgroundColor: alpha(theme.schedule.employeeAlloc, 0.12),
                                  color: theme.schedule.employeeAlloc,
                                  fontWeight: 600,
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};
